import { useState } from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  addRelative,
  patientKeys,
  removeRelative,
  updateRelative,
} from "@/api/Patients";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deducedError } from "@/Utils/ErrorUtils";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import {
  FilledButton,
  OutlinedButton,
  PlainButton,
} from "@medoma/components/Button/Button";
import { useDialog } from "@medoma/components/Dialog/useDialog";
import BinIcon from "@medoma/components/icons/BinIcon";
import PlusIcon from "@medoma/components/icons/PlusIcon";
import InputField from "@medoma/components/InputField/InputField";
import Checkbox from "@/components/Checkbox/Checkbox";
import Form from "@/components/Form/Form";
import type { IFormRelative, IRelative } from "@medoma/models/patients";
import {
  localizePhoneNumber,
  standardizePhoneNumber,
  type LocalizedPhoneNumber,
} from "@medoma/models/phoneNumbers";
import { useUserProfile } from "@/api/Users";
import { generateRandomUUID } from "@/Utils/UniqueId";
import styles from "./EditRelatives.module.scss";

const EMPTY_PHONE_NUMBER = "" as LocalizedPhoneNumber;

const draftRelative = (id: string): IFormRelative => ({
  id,
  name: "",
  phoneNumber: EMPTY_PHONE_NUMBER,
  relation: "",
  isLivingTogether: false,
});

const RelativeForm = ({
  patientId,
  relativeId,
  currentRelative,
  onDropDraft,
}: {
  patientId: string;
  relativeId: string;
  currentRelative?: IRelative;
  onDropDraft?: () => void;
}) => {
  const { t } = useLingui();
  const { countryCode } = useUserProfile();
  const queryClient = useQueryClient();
  const { confirm } = useDialog();
  const isExistingRelative = currentRelative !== undefined;

  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    register,
    reset,
    setError,
  } = useForm<IFormRelative>({
    defaultValues: currentRelative
      ? {
          id: currentRelative.id,
          name: currentRelative.name,
          phoneNumber: currentRelative.phoneNumber
            ? localizePhoneNumber({
                countryCode,
                phoneNumber: currentRelative.phoneNumber,
              })
            : EMPTY_PHONE_NUMBER,
          relation: currentRelative.relation,
          isLivingTogether: currentRelative.isLivingTogether,
        }
      : draftRelative(relativeId),
  });

  const invalidatePatient = () =>
    queryClient.invalidateQueries({ queryKey: patientKeys.detail(patientId) });

  const saveRelative = useMutation({
    mutationFn: (relative: IRelative) =>
      isExistingRelative
        ? updateRelative(patientId, relative)
        : addRelative(patientId, relative),
    onError: (error) => {
      setError("root.server", { message: deducedError(error) });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      onDropDraft?.();
      await invalidatePatient();
      if (isExistingRelative) {
        reset(getValues());
      }
    },
  });

  const removeSavedRelative = useMutation({
    mutationFn: () => removeRelative(patientId, relativeId),
    onSuccess: invalidatePatient,
  });

  const isBusy = saveRelative.isPending || removeSavedRelative.isPending;

  const handleRemove = async () => {
    if (!currentRelative) {
      onDropDraft?.();
      return;
    }
    const relativeName = currentRelative.name;
    const confirmed = await confirm({
      title: t`Ta bort anhörig`,
      message: t`Är du säker på att du vill ta bort ${relativeName}? Det går inte att ångra.`,
      confirmLabel: t`Ta bort`,
    });
    if (confirmed) {
      removeSavedRelative.mutate();
    }
  };

  return (
    <Form
      onSubmit={handleSubmit((formData) => {
        saveRelative.mutate({
          ...formData,
          phoneNumber: formData.phoneNumber
            ? standardizePhoneNumber({
                countryCode,
                phoneNumber: formData.phoneNumber,
              })
            : null,
        });
      })}
    >
      <div className={styles.inputFields}>
        <InputField
          label={t`Namn`}
          showOptionalLabel={false}
          errorMessage={errors.name?.message}
          {...register("name", {
            required: {
              value: true,
              message: t`Ange namn`,
            },
          })}
        />
        <InputField
          label={t`Telefonnummer`}
          showOptionalLabel={true}
          errorMessage={errors.phoneNumber?.message}
          {...register("phoneNumber", {
            pattern: {
              value: /^([0-9]|\s)+$/,
              message: t`Telefonnumret får bara innehålla siffror`,
            },
          })}
        />
        <InputField
          label={t`Relation`}
          showOptionalLabel={false}
          errorMessage={errors.relation?.message}
          {...register("relation", {
            required: {
              value: true,
              message: t`Ange relation`,
            },
          })}
        />
      </div>
      <Checkbox
        label={{ text: t`Sammanboende` }}
        {...register("isLivingTogether")}
      />

      {errors.root?.server?.message && !isDirty ? (
        <ErrorMessage message={errors.root.server.message} />
      ) : null}
      {removeSavedRelative.isError ? (
        <ErrorMessage message={deducedError(removeSavedRelative.error)} />
      ) : null}

      <div className={styles.relativeActions}>
        <FilledButton type="submit" disabled={isBusy || !isDirty}>
          <Trans>Spara ändringar</Trans>
        </FilledButton>

        <OutlinedButton type="button" onClick={handleRemove} disabled={isBusy}>
          <BinIcon />
          <Trans>Ta bort anhörig</Trans>
        </OutlinedButton>
      </div>
    </Form>
  );
};

export const EditRelatives = ({
  patientId,
  currentRelatives,
}: {
  patientId: string;
  currentRelatives: IRelative[];
}) => {
  const [draftIds, setDraftIds] = useState<string[]>([]);

  const addDraftId = () => setDraftIds((ids) => [...ids, generateRandomUUID()]);

  const dropDraftId = (draftId: string) =>
    setDraftIds((ids) => ids.filter((id) => id !== draftId));

  return (
    <div className={styles.editContainers}>
      {currentRelatives.map((relative) => (
        <div key={relative.id} className={styles.editRelativeContainer}>
          <RelativeForm
            patientId={patientId}
            relativeId={relative.id}
            currentRelative={relative}
          />
        </div>
      ))}

      {draftIds.map((draftId) => (
        <div key={draftId} className={styles.editRelativeContainer}>
          <RelativeForm
            patientId={patientId}
            relativeId={draftId}
            onDropDraft={() => dropDraftId(draftId)}
          />
        </div>
      ))}

      <div className={styles.addRelativeContainer}>
        <PlainButton type="button" onClick={addDraftId}>
          <PlusIcon />
          <Trans>Lägg till anhörig</Trans>
        </PlainButton>
      </div>
    </div>
  );
};
